import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Filter, X, Flame, Clock, AlertTriangle, CalendarDays, ListChecks, CheckCircle2, Trash2, Zap, Save, StickyNote, Search } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import NotifyClientDialog from "@/components/NotifyClientDialog";

const departments = ["GST", "Income Tax", "ROC", "Accounting", "Audit", "TDS", "Litigation"];
const priorities = ["Critical", "High", "Medium", "Low"];
const statuses = ["Not Started", "In Progress", "Waiting for Client", "Waiting for Department", "Completed", "Cancelled"];

const viewMeta = {
  overdue:         { label: "Overdue tasks", icon: Flame, tone: "text-red-600 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900" },
  due_today:       { label: "Tasks due today", icon: AlertTriangle, tone: "text-amber-700 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900" },
  due_tomorrow:    { label: "Tasks due tomorrow", icon: Clock, tone: "text-amber-700 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900" },
  due_week:        { label: "Tasks due this week", icon: CalendarDays, tone: "text-blue-700 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900" },
  pending:         { label: "All pending tasks", icon: ListChecks, tone: "text-slate-700 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800" },
  completed_today: { label: "Completed today", icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900" },
};

const statusColors = {
  "Not Started": "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  "Waiting for Client": "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  "Waiting for Department": "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  "Completed": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  "Cancelled": "bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-500",
};
const priorityColors = {
  "Critical": "text-red-600",
  "High": "text-orange-600",
  "Medium": "text-blue-600",
  "Low": "text-slate-500",
};

export default function Tasks() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ status: "", department: "", priority: "", assigned_to: "", client_id: "" });
  const [clientSearch, setClientSearch] = useState("");
  const [selected, setSelected] = useState({});  // task_id -> true
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null);
  const [billDialog, setBillDialog] = useState(null); // { task, status }
  const [billForm, setBillForm] = useState({ amount: "", gst_rate: 0, description: "" });
  const [notifyFor, setNotifyFor] = useState(null); // { task, client }
  const [quickTitle, setQuickTitle] = useState("");
  const [quickClient, setQuickClient] = useState("");
  const [tab, setTab] = useState("ongoing");
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view");
  const urlStatus = searchParams.get("status");
  const [form, setForm] = useState({
    title: "", description: "", department: "GST", priority: "Medium", status: "Not Started",
    assigned_to: "", client_id: "", due_date: "",
  });

  async function load() {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    if (view) params.append("view", view);
    if (urlStatus && !filters.status) params.append("status", urlStatus);
    const { data } = await api.get(`/tasks?${params}`);
    setRows(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, view, urlStatus]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data));
    api.get("/clients").then(({ data }) => setClients(data));
  }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.client_id) delete payload.client_id;
      if (!payload.due_date) delete payload.due_date;
      await api.post("/tasks", payload);
      toast.success("Task created");
      setOpen(false);
      setForm({ title: "", description: "", department: "GST", priority: "Medium", status: "Not Started", assigned_to: "", client_id: "", due_date: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function updateStatus(id, status) {
    // Intercept completion → prompt for billing (unless task already resolved for billing)
    const task = rows.find(r => r.id === id);
    if (status === "Completed" && task && !task.billing_status) {
      setBillDialog({ task, status });
      return;
    }
    await api.patch(`/tasks/${id}`, { status });
    // If just completed, also offer to notify client
    if (status === "Completed" && task?.client_id) {
      await maybeOfferNotify(task);
    }
    load();
  }

  async function maybeOfferNotify(task) {
    try {
      const { data: client } = await api.get(`/clients/${task.client_id}`);
      if (client.mobile || client.email) {
        setNotifyFor({ task, client });
      }
    } catch (err) { console.error("Failed to load client for notification prompt", err); }
  }

  async function confirmBilling(mode) {
    const { task } = billDialog;
    try {
      if (mode === "not_applicable") {
        await api.patch(`/tasks/${task.id}`, { status: "Completed", billing_status: "not_applicable" });
        toast.success("Task completed · not billable");
      } else {
        // mode === "bill"
        const amount = parseFloat(billForm.amount);
        if (!(amount > 0)) { toast.error("Enter a valid amount"); return; }
        if (!task.client_id) { toast.error("Task has no client — cannot invoice"); return; }
        const invRes = await api.post("/invoices", {
          client_id: task.client_id,
          task_id: task.id,
          description: billForm.description || task.title,
          amount,
          gst_rate: parseFloat(billForm.gst_rate) || 0,
        });
        await api.patch(`/tasks/${task.id}`, { status: "Completed" });
        toast.success(`Invoice ${invRes.data.number} generated`);
      }
      const completedTask = task;
      setBillDialog(null);
      setBillForm({ amount: "", gst_rate: 0, description: "" });
      // Offer notification
      if (completedTask.client_id) {
        await maybeOfferNotify(completedTask);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function reassign(id, assigned_to) {
    await api.patch(`/tasks/${id}`, { assigned_to });
    toast.success("Task reassigned");
    load();
    if (detailOpen) setDetailOpen({ ...detailOpen, assigned_to });
  }

  function clearView() {
    setSearchParams({});
  }

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.client_name]));
  const activeView = view && viewMeta[view];

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return [];
    return clients.filter(c =>
      (c.client_name || "").toLowerCase().includes(q) ||
      (c.gstin || "").toLowerCase().includes(q) ||
      (c.pan || "").toLowerCase().includes(q) ||
      (c.client_code || "").toLowerCase().includes(q)
    );
  }, [clientSearch, clients]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  async function bulkDelete() {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} tasks? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/tasks/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} tasks`);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  }

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Task management</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Tasks</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" />New Task</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Title *</Label><Input data-testid="task-title-input" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label>
                  <Select value={form.department} onValueChange={v => setForm({ ...form, department: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Assign To</Label>
                  <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                    <SelectTrigger data-testid="task-assign-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Note (shown in task list)</Label>
                <Input data-testid="task-note-input" placeholder="e.g. Waiting for TAN update, priority for month-end" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <DialogFooter><Button data-testid="save-task-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">Create task</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {activeView && (
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-md border ${activeView.tone}`} data-testid="active-view-banner">
          <activeView.icon className="w-4 h-4" />
          <div className="text-sm font-medium">{activeView.label}</div>
          <span className="font-mono text-xs tabular-nums opacity-70">· {rows.length}</span>
          <button onClick={clearView} className="ml-auto text-xs hover:underline flex items-center gap-1" data-testid="clear-view-btn">
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <Zap className="w-4 h-4 text-blue-600" />
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!quickTitle.trim()) return;
            try {
              const payload = { title: quickTitle.trim(), department: "GST", priority: "Medium", status: "Not Started" };
              if (quickClient) payload.client_id = quickClient;
              await api.post("/tasks", payload);
              setQuickTitle("");
              toast.success("Task added");
              load();
            } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
          }}
          className="flex items-center gap-2 flex-1"
        >
          <Input
            data-testid="quick-add-title"
            placeholder='Quick add — type task title & press Enter (e.g. "File GSTR-3B for Sharma")'
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            className="h-8 flex-1"
          />
          <Select value={quickClient || "__none__"} onValueChange={(v) => setQuickClient(v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Client (opt.)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No client</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="quick-add-btn" type="submit" size="sm" disabled={!quickTitle.trim()} className="h-8">Add</Button>
        </form>
      </Card>

      {/* Tabs: Ongoing / Completed */}
      <div className="flex items-center gap-1 border-b border-border">
        <button
          data-testid="tab-ongoing"
          onClick={() => setTab("ongoing")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "ongoing" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Ongoing <span className="ml-1.5 font-mono text-xs tabular-nums">{rows.filter(t => t.status !== "Completed" && t.status !== "Cancelled").length}</span>
        </button>
        <button
          data-testid="tab-completed"
          onClick={() => setTab("completed")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "completed" ? "border-emerald-600 text-emerald-600" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Completed <span className="ml-1.5 font-mono text-xs tabular-nums">{rows.filter(t => t.status === "Completed").length}</span>
        </button>
      </div>

      <Card className="p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          {[
            { key: "status", options: statuses },
            { key: "department", options: departments },
            { key: "priority", options: priorities },
          ].map(({ key, options }) => (
            <Select key={key} value={filters[key] || "__all__"} onValueChange={v => setFilters({ ...filters, [key]: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid={`filter-${key}`} className="h-8 text-xs w-40"><SelectValue placeholder={`All ${key}`} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All {key}</SelectItem>
                {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          <Select value={filters.assigned_to || "__all__"} onValueChange={v => setFilters({ ...filters, assigned_to: v === "__all__" ? "" : v })}>
            <SelectTrigger data-testid="filter-user" className="h-8 text-xs w-40"><SelectValue placeholder="All users" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All users</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-muted-foreground font-mono tabular-nums">{rows.length} tasks</div>
        </div>
        {/* Client search — searchable client filter (typeahead) */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Search className="w-4 h-4 text-muted-foreground" />
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Input
              data-testid="filter-client-search"
              value={filters.client_id ? (clientMap[filters.client_id] || "") : clientSearch}
              onChange={(e) => { setClientSearch(e.target.value); if (filters.client_id) setFilters({ ...filters, client_id: "" }); }}
              placeholder="Filter by client — type name, GSTIN or PAN…"
              className="h-8 pr-8"
            />
            {(clientSearch || filters.client_id) && (
              <button
                type="button"
                data-testid="clear-client-filter"
                onClick={() => { setClientSearch(""); setFilters({ ...filters, client_id: "" }); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear client filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {clientSearch && !filters.client_id && filteredClients.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="client-search-results">
                {filteredClients.slice(0, 20).map(c => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`pick-client-${c.id}`}
                    onClick={() => { setFilters({ ...filters, client_id: c.id }); setClientSearch(""); }}
                    className="w-full text-left px-3 py-1.5 hover:bg-muted/60 text-sm flex items-center justify-between gap-2 border-b border-border last:border-0"
                  >
                    <span className="truncate">{c.client_name}</span>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">{c.gstin || c.pan || c.client_code || ""}</span>
                  </button>
                ))}
                {filteredClients.length > 20 && (
                  <div className="px-3 py-1.5 text-[10px] text-muted-foreground italic">+{filteredClients.length - 20} more · refine your search</div>
                )}
              </div>
            )}
            {clientSearch && !filters.client_id && filteredClients.length === 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-lg px-3 py-2 text-xs text-muted-foreground">
                No matching client.
              </div>
            )}
          </div>
          {filters.client_id && (
            <Badge variant="outline" className="gap-1" data-testid="client-filter-pill">
              Client: {clientMap[filters.client_id]}
              <button onClick={() => setFilters({ ...filters, client_id: "" })} className="ml-1 hover:text-red-600" aria-label="Remove client filter">×</button>
            </Badge>
          )}
        </div>
      </Card>

      {/* Bulk action bar — shows when any tasks selected */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-md border border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/30" data-testid="bulk-action-bar">
          <Checkbox checked onCheckedChange={() => setSelected({})} />
          <div className="text-sm">
            <b>{selectedCount}</b> task{selectedCount !== 1 ? "s" : ""} selected
          </div>
          <Button
            data-testid="bulk-delete-btn"
            size="sm"
            variant="destructive"
            onClick={bulkDelete}
            className="ml-auto"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected({})}>Clear</Button>
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        {(() => {
          const visible = rows.filter(t => tab === "completed"
            ? t.status === "Completed"
            : t.status !== "Completed" && t.status !== "Cancelled"
          );
          if (visible.length === 0) {
            return <div className="p-10 text-center text-sm text-muted-foreground">
              {tab === "completed" ? "No completed tasks yet." : "No ongoing tasks — you're all caught up!"}
            </div>;
          }
          return (
            <Table className="stripe-hover">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="select-all-tasks"
                      checked={visible.length > 0 && visible.every(t => selected[t.id])}
                      onCheckedChange={(v) => {
                        if (v) {
                          const s = { ...selected };
                          visible.forEach(t => { s[t.id] = true; });
                          setSelected(s);
                        } else {
                          const s = { ...selected };
                          visible.forEach(t => { delete s[t.id]; });
                          setSelected(s);
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead className="w-40">Client</TableHead>
                  <TableHead>Task Title</TableHead>
                  <TableHead className="w-28">Department</TableHead>
                  <TableHead className="w-24">Priority</TableHead>
                  <TableHead className="w-32">Assign To</TableHead>
                  <TableHead className="w-40">Status</TableHead>
                  <TableHead className="w-20 text-right">Due</TableHead>
                  <TableHead className="w-64">Quick Note</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map(t => {
                  const overdue = t.due_date && t.due_date < new Date().toISOString().slice(0, 10) && t.status !== "Completed" && t.status !== "Cancelled";
                  return (
                    <TableRow key={t.id} data-testid={`task-row-${t.id}`} className={`align-top ${selected[t.id] ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}`}>
                      <TableCell className="pt-3">
                        <Checkbox
                          data-testid={`select-task-${t.id}`}
                          checked={!!selected[t.id]}
                          onCheckedChange={(v) => setSelected(s => ({ ...s, [t.id]: !!v }))}
                        />
                      </TableCell>
                      <TableCell className="text-xs pt-2.5">
                        <div className="whitespace-normal break-words leading-snug">
                          {t.client_id ? clientMap[t.client_id] : <span className="text-muted-foreground italic">No client</span>}
                        </div>
                      </TableCell>
                      <TableCell className="pt-2 cursor-pointer group" onClick={() => setDetailOpen(t)}>
                        <div className="text-sm font-medium whitespace-normal break-words leading-snug group-hover:text-blue-600 transition-colors">
                          {t.title}
                        </div>
                        {t.description && (
                          <div className="text-[10px] text-muted-foreground whitespace-normal break-words line-clamp-2 mt-0.5">
                            {t.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="pt-2.5">
                        <Badge variant="outline" className="text-[10px] font-mono">{t.department}</Badge>
                      </TableCell>
                      <TableCell className="pt-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${priorityColors[t.priority]?.replace("text-", "bg-") || "bg-slate-300"}`} />
                          <span className={`text-[11px] ${priorityColors[t.priority] || "text-muted-foreground"}`}>{t.priority}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs pt-2.5 whitespace-normal break-words">
                        {t.assigned_to ? userMap[t.assigned_to] : <span className="text-muted-foreground italic">Unassigned</span>}
                      </TableCell>
                      <TableCell className="pt-2">
                        <Select value={t.status} onValueChange={v => updateStatus(t.id, v)}>
                          <SelectTrigger className={`h-7 text-[11px] w-full border-0 ${statusColors[t.status]}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className={`text-right font-mono text-[11px] tabular-nums pt-2.5 ${overdue ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
                        {t.due_date ? format(parseISO(t.due_date), "dd MMM") : "—"}
                      </TableCell>
                      <TableCell className="pt-2">
                        <InlineNoteCell task={t} onSaved={(newNote) => {
                          setRows(rs => rs.map(r => r.id === t.id ? { ...r, notes: newNote } : r));
                        }} />
                      </TableCell>
                      <TableCell className="pt-2.5">
                        <button
                          data-testid={`delete-task-${t.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!confirm(`Delete task "${t.title}"?`)) return;
                            await api.delete(`/tasks/${t.id}`);
                            toast.success("Deleted");
                            load();
                          }}
                          className="text-muted-foreground hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          );
        })()}
      </Card>

      {/* Detail */}
      <Dialog open={!!detailOpen} onOpenChange={o => !o && setDetailOpen(null)}>
        <DialogContent className="max-w-lg">
          {detailOpen && (
            <>
              <DialogHeader><DialogTitle>{detailOpen.title}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex gap-2 flex-wrap">
                  <Badge>{detailOpen.department}</Badge>
                  <Badge variant="outline">{detailOpen.priority}</Badge>
                  <Badge>{detailOpen.status}</Badge>
                  {detailOpen.billing_status === "billed" && detailOpen.invoice_number && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">Invoice {detailOpen.invoice_number}</Badge>
                  )}
                  {detailOpen.billing_status === "not_applicable" && (
                    <Badge variant="outline" className="text-muted-foreground">Not billable</Badge>
                  )}
                </div>
                <div className="text-muted-foreground">{detailOpen.description || "No description."}</div>
                <div className="pt-3 border-t border-border space-y-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reassign to</Label>
                    <Select value={detailOpen.assigned_to || ""} onValueChange={v => reassign(detailOpen.id, v)}>
                      <SelectTrigger data-testid="reassign-select" className="mt-1"><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-muted-foreground mt-1">Any team member (including your manager) can be assigned.</div>
                  </div>
                  <div className="text-xs font-mono grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Client:</span> {clientMap[detailOpen.client_id] || "—"}</div>
                    <div><span className="text-muted-foreground">Due:</span> {detailOpen.due_date || "—"}</div>
                  </div>
                  {detailOpen.client_id && (
                    <Button
                      data-testid="notify-from-detail-btn"
                      size="sm"
                      variant="outline"
                      className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-300"
                      onClick={async () => {
                        const { data: client } = await api.get(`/clients/${detailOpen.client_id}`);
                        setNotifyFor({ task: detailOpen, client });
                        setDetailOpen(null);
                      }}
                    >
                      Notify client (WhatsApp / Email)
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Billing on completion dialog */}
      <Dialog open={!!billDialog} onOpenChange={o => !o && setBillDialog(null)}>
        <DialogContent className="max-w-md">
          {billDialog && (
            <>
              <DialogHeader><DialogTitle>Complete &amp; bill?</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  You're marking <span className="font-medium text-foreground">"{billDialog.task.title}"</span> as completed.
                  {billDialog.task.client_id
                    ? " Do you want to raise an invoice to " + (clientMap[billDialog.task.client_id] || "the client") + "?"
                    : " This task has no client, so it can't be billed."}
                </div>

                {billDialog.task.client_id && (
                  <div className="space-y-3 border border-border rounded-md p-3">
                    <div><Label>Bill Amount (₹, excl. GST) *</Label>
                      <Input data-testid="bill-amount-input" type="number" step="0.01" autoFocus value={billForm.amount} onChange={e => setBillForm({ ...billForm, amount: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>GST Rate (%)</Label>
                        <Input type="number" value={billForm.gst_rate} onChange={e => setBillForm({ ...billForm, gst_rate: e.target.value })} />
                      </div>
                      <div className="flex items-end">
                        <div className="text-[11px] text-muted-foreground">
                          Total: <span className="font-mono font-semibold text-foreground">
                            ₹{(((parseFloat(billForm.amount) || 0) * (1 + (parseFloat(billForm.gst_rate) || 0) / 100)) || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div><Label>Description (optional)</Label>
                      <Input value={billForm.description} onChange={e => setBillForm({ ...billForm, description: e.target.value })} placeholder={billDialog.task.title} />
                    </div>
                  </div>
                )}

                <DialogFooter className="gap-2">
                  <Button data-testid="bill-skip-btn" variant="outline" onClick={() => confirmBilling("not_applicable")}>
                    Not applicable
                  </Button>
                  {billDialog.task.client_id && (
                    <Button data-testid="bill-generate-btn" onClick={() => confirmBilling("bill")} className="bg-emerald-600 hover:bg-emerald-700">
                      Generate invoice
                    </Button>
                  )}
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Notify client dialog */}
      <NotifyClientDialog
        open={!!notifyFor}
        onClose={() => setNotifyFor(null)}
        client={notifyFor?.client}
        task={notifyFor?.task}
        reason="task_completed"
      />
    </div>
  );
}

function InlineNoteCell({ task, onSaved }) {
  const [val, setVal] = useState(task.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(task.notes || ""); }, [task.id, task.notes]);
  const dirty = (val || "") !== (task.notes || "");
  async function save() {
    if (!dirty || saving) return;
    try {
      setSaving(true);
      await api.patch(`/tasks/${task.id}`, { notes: val });
      toast.success("Note saved");
      onSaved?.(val);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }
  return (
    <div className="flex items-start gap-1.5">
      <StickyNote className="w-3 h-3 text-amber-600 shrink-0 mt-1.5" />
      <Textarea
        data-testid={`inline-note-${task.id}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); } }}
        placeholder="Waiting for docs, priority…"
        rows={2}
        className={`text-[11px] leading-snug min-h-[36px] resize-y bg-transparent border-dashed ${dirty ? "border-amber-400 focus-visible:ring-amber-500" : "border-border/60"}`}
      />
      <Button
        data-testid={`save-note-${task.id}`}
        type="button"
        size="icon"
        variant={dirty ? "default" : "ghost"}
        disabled={!dirty || saving}
        onClick={save}
        className={`h-6 w-6 shrink-0 ${dirty ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
        title={dirty ? "Save note (Ctrl+Enter)" : "No changes"}
      >
        <Save className="w-3 h-3" />
      </Button>
    </div>
  );
}
